import React, { useCallback, useEffect, useState } from 'react';
import Head from 'next/head';
import Axios from 'axios';
import getMetaTagData from '../../src/common/services/getMetaTagData';
import Title from '../../src/component/pc/common/Title';
import Loading from '../../src/component/pc/common/Loading';
import LiveChannelList from '../../src/component/pc/live/LiveChannelList';
import LiveChannelTvAndVirtual from '../../src/component/pc/live/LiveChannelTvAndVirtual';
import LiveChannelVirtual from '../../src/component/pc/live/LiveChannelVirtual';

import EventBanner from '../../src/component/pc/live/banner/EventBanner';
import AdGenerater from '../../src/component/pc/ads/AdGenerater';

import { deduplArray, deduplChannelArray } from '../../src/common/utils/convertUtil';
import { toPlanUrl } from '../../src/common/utils/liveUtil';

const LiveListPage = ({ tv, radio, virtual, virtual_curation, virtual_curation_banner, event, metaTagData }) => {
  const [type, setType] = useState(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const paramsChannelType = params.get('type');

    const is24HourTv = paramsChannelType?.toLocaleLowerCase() === '24tv';
    if (is24HourTv) setType('24TV');
  }, []);

  return (
    <div className='contents'>
      <Head>
        <link key='css_pc_url' rel='stylesheet' href={process.env.NEXT_PUBLIC_GLOBAL_CSS_PC_URL} />
        <meta key='viewport' name='viewport' content='width=device-width, initial-scale=1' />
        <script key='gnb_js_url' type='text/javascript' src={process.env.NEXT_PUBLIC_GNB_JS_URL} />

        <meta name='description' content={metaTagData.shareDescription} />
        <meta name='keywords' content={metaTagData.keywords} />
        <meta name='twitter:card' content='summary' />
        <noscript>Your browser does not support JavaScript!</noscript>
        <meta property='og:type' content='website' />
        <meta property='og:locale' content='ko_KR' />
        <meta property='og:site_name' content='SBS' />
        <meta property='og:title' content={metaTagData.shareTitle || ''} />
        <meta property='og:description' content={metaTagData.shareDescription || ''} />
        <meta property='og:image' content={metaTagData.shareImageUrl || ''} />
        <meta property='og:url' content={metaTagData.currentUrl || ''} />
        <meta name='nate:site_name' content='SBS' />
        <meta name='nate:title' content={metaTagData.shareTitle || ''} />
        <meta name='nate:description' content={metaTagData.shareDescription || ''} />
        <meta name='nate:image' content={metaTagData.shareImageUrl || ''} />
        <meta name='nate:url' content={metaTagData.currentUrl || ''} />
        <title>{metaTagData.shareTitle}</title>
      </Head>
      <div role='main' id='container' className={type === '24TV' ? 'tv24_listcontents_wrap' : 'live_listcontents_wrap'} >
        <h2 className='hide'>라이브</h2>

        {type !== null && type.toUpperCase() === '24TV' && <AdGenerater id='live_listcontents_topAD' w='930' h='180' />}
        {type === null && event && (<EventBanner {...event} />)}

        <LiveChannelTvAndVirtual type={type} tv={tv} virtual={virtual} virtual_curation={virtual_curation} virtual_curation_banner={virtual_curation_banner} />

        {type === null && <div className='contents_area_liveradio'>
          <Title title='라이브 라디오' desc='편성표' link={toPlanUrl('radio')} />
          <div className='live_list_w'>
            {!radio && <Loading />}
            {radio && (<LiveChannelList clickParams='div=live_list' data={radio} />)}
          </div>
        </div>}

        {type !== null && type.toUpperCase() === '24TV' && <AdGenerater id='24live_web_bottom_970_90' w='970' h='90' />}
        {type === null && <AdGenerater id='live' />}

        {type === null && <LiveChannelVirtual type={type} virtual={virtual} />}

      </div>
    </div>
  );
};

export async function getServerSideProps (ctx) {
  try {
    // header에서 meta teg에 넣어줄 데이터를 가져 오기 위함
    const metaTagData = getMetaTagData(ctx.req.url);
    const [tv, radio, virtual, virtual_curation, virtual_curation_banner, event] = await Promise.all([
      tvChannelData(),
      radioChannelData(),
      virtualChannelData(),
      virtualCurationData(),
      virtualCurationBannerData(),
      eventData()
    ]);

    if (Array.isArray(virtual) && virtual.length > 0) {
      const virtualTitleList = virtual.map(item => item.channelname);
      metaTagData.keywords = metaTagData.keywords 
        ? `${metaTagData.keywords},${virtualTitleList.toString()}`
        : virtualTitleList.toString();
    }

    return {
      props: {
        tv: tv || null,
        radio: radio || null,
        virtual: virtual || null,
        virtual_curation: virtual_curation || null,
        virtual_curation_banner: virtual_curation_banner || null,
        event,
        metaTagData
      }
    };
  } catch (e) {
    console.error('getServerSideProps:', e);
    return { props: {} };
  }
}

const fetchData = async (url) => {
  try {
    const response = await Axios.get(url, { timeout: 3000 });
    return response.data;
  } catch (e) {
    console.error(`Error fetching data from ${url}:`, e);
    return null;
  }
};

const tvChannelData = async () => {
  const data = await fetchData(process.env.NEXT_PUBLIC_API_LIVE_CHANNELS);
  if (!data) return null;
  let liveTVChannelArray = data.list.filter((item) => item.type === 'TV');
  return deduplChannelArray(liveTVChannelArray, 'channelname', 'channelid');
};

const radioChannelData = async () => {
  const data = await fetchData(process.env.NEXT_PUBLIC_API_LIVE_CHANNELS);
  if (!data) return null;
  const dupRadioArray = data.list.filter((item) => item.type === 'Radio' && item.onair_yn === 'Y');
  return deduplArray(dupRadioArray, 'channelname', 'channelid');
};

const virtualChannelData = async () => {
  const data = await fetchData(`${process.env.NEXT_PUBLIC_API_LIVE_CHANNELS_24TV}?v_type=2`);
  if (!data) return null;
  return data.list.filter(item => item.channelname && item.channelid);
};

const virtualCurationData = async () => {
  const data = await fetchData(process.env.NEXT_PUBLIC_API_LIVE_CHANNELS_24TV_CURATION);
  if (!data) return null;
  return data.list.filter(item => item.service_yn && item.service_yn.toUpperCase() === 'Y');
};

const virtualCurationBannerData = async () => {
  const data = await fetchData(process.env.NEXT_PUBLIC_API_LIVE_CHANNELS_24TV_CURATION_BANNER);
  if (!data) return null;
  data.formation_title_svc = 'N';
  return data;
};

const eventData = async () => {
  return await fetchData(process.env.NEXT_PUBLIC_API_LIVE_FORMATION_EVENT);
};

export default LiveListPage;
